import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Divider,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { apiClient } from '../services/api';

const TestCheckins = () => {
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    userName: '',
    userEmail: '',
    notes: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [isDev, setIsDev] = useState(false);

  useEffect(() => {
    // Verificar si estamos en entorno de desarrollo
    const isDevEnv = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname.includes('127.0.0.1');
    
    setIsDev(isDevEnv);
    
    if (!isDevEnv) {
      return; // No cargar datos si no estamos en desarrollo
    }
    
    // Cargar los partners
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      // Primero intentamos un endpoint específico para obtener todos los partners
      const partnersResponse = await apiClient.get('/partners/all');
      
      if (partnersResponse.data && partnersResponse.data.status === 'success') {
        setPartners(partnersResponse.data.data || []);
      } else {
        // Si falla, usamos un enfoque alternativo: consultamos los partners registrados
        const response = await apiClient.get('/register/partner/list');
        if (response.data && response.data.status === 'success') {
          setPartners(response.data.data || []);
        } else {
          // Si ambos fallan, usamos datos de ejemplo
          console.warn('No se pudieron obtener partners reales, usando datos de ejemplo');
          setPartners([
            { id: 1, partnername: 'VivaGym Madrid', email: 'vivagym@example.com' },
            { id: 2, partnername: 'FitnessFirst Barcelona', email: 'fitness@example.com' },
            { id: 3, partnername: 'CrossFit Valencia', email: 'crossfit@example.com' }
          ]);
        }
      }
    } catch (error) {
      console.error('Error obteniendo partners:', error);
      // Usar datos de ejemplo como fallback
      setPartners([
        { id: 1, partnername: 'VivaGym Madrid', email: 'vivagym@example.com' },
        { id: 2, partnername: 'FitnessFirst Barcelona', email: 'fitness@example.com' },
        { id: 3, partnername: 'CrossFit Valencia', email: 'crossfit@example.com' }
      ]);
      setSnackbar({
        open: true,
        message: 'Error cargando partners. Usando datos de ejemplo.',
        severity: 'warning'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerSelect = (partner) => {
    setSelectedPartner(partner);
    // Actualizar email del form con el del partner
    setFormData({
      ...formData,
      partnerEmail: partner.email
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmitCheckin = async (e) => {
    e.preventDefault();
    
    if (!selectedPartner) {
      setSnackbar({
        open: true,
        message: 'Por favor, selecciona un partner primero',
        severity: 'error'
      });
      return;
    }
    
    if (!formData.userName) {
      setSnackbar({
        open: true,
        message: 'El nombre de usuario es obligatorio',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Crear datos del check-in con los nombres de campo correctos (sin guiones bajos)
      const checkInData = {
        userid: `user-${Date.now()}`,
        username: formData.userName,
        useremail: formData.userEmail || 'user@example.com',
        partnerid: selectedPartner.id.toString(),
        partnername: selectedPartner.partnername,
        partneremail: selectedPartner.email,
        location: formData.location || selectedPartner.partnername,
        notes: formData.notes || '',
        timestamp: new Date().toISOString()
      };
      
      console.log('Enviando check-in con datos:', checkInData);
      
      // Enviar el check-in al endpoint
      const response = await apiClient.post('/check-ins', checkInData);
      
      if (response.data && (response.data.status === 'success' || response.data.success)) {
        setSnackbar({
          open: true,
          message: 'Check-in enviado con éxito',
          severity: 'success'
        });
        
        // Limpiar el formulario pero mantener el partner seleccionado
        setFormData({
          userName: '',
          userEmail: '',
          notes: '',
          location: '',
          partnerEmail: selectedPartner.email
        });
      } else {
        throw new Error(response.data.message || 'Error al procesar el check-in');
      }
    } catch (error) {
      console.error('Error enviando check-in:', error);
      console.error('Detalles del error:', error.response?.data || 'No hay detalles adicionales');
      setSnackbar({
        open: true,
        message: `Error: ${error.message || 'No se pudo enviar el check-in'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Si no estamos en desarrollo, no mostrar nada
  if (!isDev) {
    return (
      <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
        <Alert severity="error">
          Esta página sólo está disponible en entorno de desarrollo.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
      <Typography variant="h4" gutterBottom>
        Test de Check-ins
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        Esta página solo está disponible en entorno de desarrollo.
      </Typography>
      
      <Divider sx={{ mb: 4 }} />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Lista de partners */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Lista de Partners
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {partners.map((partner) => (
                      <TableRow 
                        key={partner.id}
                        sx={{ 
                          backgroundColor: selectedPartner?.id === partner.id 
                            ? 'rgba(25, 118, 210, 0.08)' 
                            : 'inherit'
                        }}
                      >
                        <TableCell>{partner.id}</TableCell>
                        <TableCell>{partner.partnername}</TableCell>
                        <TableCell>{partner.email}</TableCell>
                        <TableCell>
                          <Button 
                            variant={selectedPartner?.id === partner.id ? "contained" : "outlined"}
                            size="small"
                            onClick={() => handlePartnerSelect(partner)}
                          >
                            {selectedPartner?.id === partner.id ? "Seleccionado" : "Seleccionar"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {partners.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No se encontraron partners. Intenta actualizar la página.
                </Alert>
              )}
            </Paper>
          </Grid>
          
          {/* Formulario de check-in */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Crear Check-in
              </Typography>
              
              {!selectedPartner ? (
                <Alert severity="info">
                  Selecciona un partner de la lista para crear un check-in
                </Alert>
              ) : (
                <Box component="form" onSubmit={handleSubmitCheckin} noValidate>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                    Partner seleccionado: <Chip label={selectedPartner.partnername} color="primary" />
                  </Typography>
                  
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="userName"
                    label="Nombre de usuario"
                    name="userName"
                    value={formData.userName}
                    onChange={handleInputChange}
                  />
                  
                  <TextField
                    margin="normal"
                    fullWidth
                    id="userEmail"
                    label="Email de usuario (opcional)"
                    name="userEmail"
                    value={formData.userEmail}
                    onChange={handleInputChange}
                  />
                  
                  <TextField
                    margin="normal"
                    fullWidth
                    id="location"
                    label="Ubicación (opcional)"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder={selectedPartner.partnername}
                  />
                  
                  <TextField
                    margin="normal"
                    fullWidth
                    id="notes"
                    label="Notas (opcional)"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                  />
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : "Enviar Check-in"}
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={closeSnackbar}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TestCheckins; 